import { useEffect, useState, useRef } from "react";
import { Package, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { hourBankName } from "../../lib/hourbank";

/**
 * RC3 Lite — Banner inteligente de pacote de horas.
 * Consulta pacotes ativos do cliente (email/cpf) e exibe:
 *  - saldo atual do pacote
 *  - simulação de consumo para a reserva atual (hoursNeeded)
 *  - alertas de saldo baixo/zerado
 *  - mensagem sobre pagamento (pacote cobre / não cobre)
 *
 * READ-ONLY. Reutiliza `client_packages` via GET /public/packages/lookup.
 * Não altera integrações, DB, WhatsApp, MP ou webhooks.
 */
export default function PackageBanner({ email, cpf, hoursNeeded }) {
  const [loading, setLoading] = useState(false);
  const [best, setBest] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const e = (email || "").trim().toLowerCase();
    const c = (cpf || "").replace(/\D/g, "");
    const hasEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    const hasCpf = c.length === 11;
    if (!hasEmail && !hasCpf) {
      setBest(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const params = {};
        if (hasEmail) params.email = e;
        if (hasCpf) params.cpf = c;
        const { data } = await api.get("/public/packages/lookup", { params });
        setBest(data?.best_package || null);
      } catch {
        setBest(null);
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [email, cpf]);

  if (loading) {
    return (
      <div
        data-testid="package-banner-loading"
        className="rounded-xl border-2 border-gray-200 bg-white p-3 text-xs text-gray-500 flex items-center gap-2"
      >
        <Loader2 size={14} className="animate-spin" /> Verificando Banco de Horas ativo...
      </div>
    );
  }

  if (!best) return null;

  const remaining = Number(best.hours_remaining || 0);
  const needed = Number(hoursNeeded || 0);
  const afterBooking = Math.max(0, +(remaining - needed).toFixed(2));
  const covers = needed > 0 && remaining >= needed;

  // Cores/alertas baseados no saldo atual
  let alertLevel = "ok"; // ok | low | last | empty
  if (remaining <= 0) alertLevel = "empty";
  else if (remaining < 4) alertLevel = "last";
  else if (remaining < 8) alertLevel = "low";

  const validUntil = best.valid_until
    ? new Date(best.valid_until).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  // Estilos
  const wrapCls = covers
    ? "border-emerald-300 bg-emerald-50"
    : alertLevel === "empty" || alertLevel === "last"
    ? "border-amber-300 bg-amber-50"
    : "border-[#D4AF37]/50 bg-[#D4AF37]/10";

  return (
    <div
      data-testid="package-banner"
      className={`rounded-2xl border-2 ${wrapCls} p-4 space-y-3`}
    >
      <div className="flex items-start gap-3">
        <Package size={22} className="text-[#0A1128] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">
            Você possui um Banco de Horas ativo
          </div>
          <div className="font-bold text-[#0A1128] text-base" data-testid="package-plan-name">
            {hourBankName(best)}
          </div>
          {validUntil && (
            <div className="text-xs text-gray-600 mt-0.5">
              Válido até <strong>{validUntil}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Saldo do pacote */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-white/70 border border-white p-2">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
            Total
          </div>
          <div className="text-lg font-black text-[#0A1128]" data-testid="pkg-hours-total">
            {best.hours_total}h
          </div>
        </div>
        <div className="rounded-lg bg-white/70 border border-white p-2">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
            Utilizadas
          </div>
          <div className="text-lg font-black text-[#0A1128]" data-testid="pkg-hours-used">
            {best.hours_used}h
          </div>
        </div>
        <div className="rounded-lg bg-white/70 border border-white p-2">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
            Restantes
          </div>
          <div className="text-lg font-black text-emerald-700" data-testid="pkg-hours-remaining">
            {remaining}h
          </div>
        </div>
      </div>

      {/* Simulação de consumo — só quando temos horas da reserva */}
      {needed > 0 && (
        <div
          className="rounded-lg bg-white border border-gray-200 p-3 text-sm"
          data-testid="pkg-simulation"
        >
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Reserva</span>
            <span className="font-bold text-[#0A1128]" data-testid="sim-hours-needed">
              {needed}h
            </span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-gray-600">Saldo atual</span>
            <span className="font-bold text-[#0A1128]">{remaining}h</span>
          </div>
          <div className="flex items-center justify-between mt-1 border-t border-dashed border-gray-200 pt-1.5">
            <span className="text-gray-600">Saldo após a reserva</span>
            <span
              className={`font-black ${
                covers ? "text-emerald-700" : "text-amber-700"
              }`}
              data-testid="sim-hours-after"
            >
              {covers ? `${afterBooking}h` : "insuficiente"}
            </span>
          </div>
        </div>
      )}

      {/* Mensagem de pagamento */}
      {needed > 0 && covers && (
        <div
          className="rounded-lg bg-emerald-100 text-emerald-900 text-sm px-3 py-2 flex items-start gap-2"
          data-testid="pkg-msg-covered"
        >
          <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
          <span>
            Esta reserva será confirmada imediatamente. <strong>Nenhum PIX será gerado.</strong>
          </span>
        </div>
      )}
      {needed > 0 && !covers && (
        <div
          className="rounded-lg bg-blue-50 text-blue-900 text-sm px-3 py-2 flex items-start gap-2"
          data-testid="pkg-msg-not-covered"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>
            Seu pacote não possui saldo suficiente. O sistema utilizará o{" "}
            <strong>fluxo normal de pagamento via PIX</strong>.
          </span>
        </div>
      )}

      {/* Alertas de saldo */}
      {alertLevel === "low" && (
        <div
          className="text-xs font-semibold text-amber-800 flex items-center gap-1.5"
          data-testid="pkg-alert-low"
        >
          <AlertTriangle size={13} /> Seu Banco de Horas está acabando.
        </div>
      )}
      {alertLevel === "last" && (
        <div
          className="text-xs font-semibold text-amber-900 flex items-center gap-1.5"
          data-testid="pkg-alert-last"
        >
          <AlertTriangle size={13} /> Últimas horas disponíveis. Garanta seu próximo Banco de Horas.
        </div>
      )}
      {alertLevel === "empty" && (
        <div
          className="text-xs font-semibold text-red-800 flex items-center gap-1.5"
          data-testid="pkg-alert-empty"
        >
          <AlertTriangle size={13} /> Seu Banco de Horas foi totalmente utilizado.
        </div>
      )}
    </div>
  );
}
